import { useRuntimeConfig } from '#app';
import type { RouteLocationNormalized } from 'vue-router';
import { DEFAULT_LOCALE, LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE_SECONDS, type LocaleCode } from '~/constants/i18n';
import {
  decideLocaleAction,
  isLocaleCode,
  isStaffPath,
  localeFromRouteName,
  localizePath,
  parseEnabledLocales,
  type LocaleAction,
  type LocalePrefs,
} from '~/utils/i18n/locale';

// Module-level (not per-call state): `switchLocale` below already updates
// `authStore.user.languageIso`, which the plugin's `languageIso` watcher
// (plugins/i18n-locale.client.ts) also reacts to by re-running `sync()` — without this
// guard that watcher fires mid-switch and double-navigates. Set for the duration of
// `switchLocale` only; the watcher checks it via `isSwitchingLocale()`.
let switching = false;

/** True while `switchLocale` (below) is applying a locale change. See the module comment. */
export const isSwitchingLocale = () => switching;

export const useLocaleSwitch = () => {
  const nuxtApp = useNuxtApp();
  const router = useRouter();
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  const { catchErrors } = useErrorHandler();
  const cookie = useCookie<string | null>(LOCALE_COOKIE, {
    maxAge: LOCALE_COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax',
    path: '/',
  });

  const enabledLocales = computed(() => parseEnabledLocales(String(config.public.enabledLocales ?? '')));
  const currentLocale = computed<LocaleCode>(() => {
    const value = nuxtApp.$i18n.locale.value;
    return isLocaleCode(value) ? value : DEFAULT_LOCALE;
  });
  // Staff tools (/admin/**, /am/**) stay English + LTR regardless of preference (spec §3.7) —
  // the switcher UI must not offer a choice there, and `switchLocale` below never writes the
  // cookie or profile on those paths even if invoked some other way.
  const canSwitchLocale = computed(
    () => enabledLocales.value.length > 1 && !isStaffPath(router.currentRoute.value.path),
  );

  const applyLocale = async (code: LocaleCode) => {
    // Runs on every client navigation from the global route middleware — a transient
    // chunk-load failure here must never reject inside the route guard. Report it and
    // leave the current locale exactly as it was. (Dates go through utils/i18n/format — no
    // client code uses dayjs, so there is no dayjs locale to sync.)
    try {
      if (nuxtApp.$i18n.locale.value === code) return;
      await nuxtApp.$i18n.loadLocaleMessages(code);
      nuxtApp.$i18n.locale.value = code;
    } catch (error) {
      catchErrors(error);
    }
  };

  // Apply a `decideLocaleAction` result. A locale chosen via the URL prefix (`persist`) is also
  // written to the cookie so unprefixed pages (signup, portal) keep the visitor's language.
  const applyAction = async (action: Extract<LocaleAction, { type: 'set' }>) => {
    if (action.persist && cookie.value !== action.locale) cookie.value = action.locale;
    await applyLocale(action.locale);
  };

  const readPrefs = (): LocalePrefs => ({
    userLanguageIso: authStore.user?.languageIso ?? null,
    cookie: cookie.value,
    browserLanguages: import.meta.client ? navigator.languages : [],
  });

  const resolveAction = (to: RouteLocationNormalized): LocaleAction =>
    decideLocaleAction({ path: to.fullPath, routeName: to.name, prefs: readPrefs(), enabled: enabledLocales.value });

  const switchLocale = async (code: LocaleCode) => {
    // Router, not useRoute(): this composable is also called from route middleware.
    const route = router.currentRoute.value;
    // Staff tools never write the locale cookie or the profile, and never flip
    // to a non-English/RTL locale — regardless of what triggered the call.
    if (isStaffPath(route.path)) return;

    switching = true;
    try {
      cookie.value = code;
      if (authStore.user) {
        // `languageIso` outranks the cookie in `decideLocaleAction`, so update it locally
        // BEFORE navigating/applying — otherwise the next navigation (or a failed PATCH)
        // resolves back to the old profile language. This also fires the plugin's
        // `languageIso` watcher, which the `switching` flag (set above) suppresses so it
        // doesn't re-run `sync()` and double-navigate on top of this call.
        authStore.updateUser({ ...authStore.user, languageIso: code });
        usePreferencesStore().persistLanguage(code).catch(catchErrors);
      }
      const routeLocale = localeFromRouteName(route.name);
      if (routeLocale) {
        await navigateTo(localizePath(route.fullPath, routeLocale, code));
        return;
      }
      await applyLocale(code);
    } finally {
      switching = false;
    }
  };

  return {
    currentLocale,
    enabledLocales,
    canSwitchLocale,
    cookie,
    applyLocale,
    applyAction,
    readPrefs,
    resolveAction,
    switchLocale,
  };
};
